/**
 * Per-step status. Drives the indicator glyph + colour and (when
 * `inProgress`) auto-renders a `Spinner` instead of a static icon.
 *   - `pending`     waiting to start (hollow circle)
 *   - `inProgress`  actively running (spinner)
 *   - `succeeded`   completed OK (filled check)
 *   - `failed`      completed with error (filled cross)
 *   - `skipped`     skipped because a precondition wasn't met (dash)
 *   - `cancelled`   user cancelled while pending or in-flight (slash)
 */
export const STEP_STATUS_PENDING = 'pending';
export const STEP_STATUS_IN_PROGRESS = 'inProgress';
export const STEP_STATUS_SUCCEEDED = 'succeeded';
export const STEP_STATUS_FAILED = 'failed';
export const STEP_STATUS_SKIPPED = 'skipped';
export const STEP_STATUS_CANCELLED = 'cancelled';

export type StepStatus =
  | typeof STEP_STATUS_PENDING
  | typeof STEP_STATUS_IN_PROGRESS
  | typeof STEP_STATUS_SUCCEEDED
  | typeof STEP_STATUS_FAILED
  | typeof STEP_STATUS_SKIPPED
  | typeof STEP_STATUS_CANCELLED;

/**
 * Density tokens. `compact` is for log-style inline lists; `regular`
 * (default) is for build/run progress panels; `comfortable` is for
 * onboarding wizards where each step needs room to breathe.
 */
export const STEP_LIST_DENSITY_COMPACT = 'compact';
export const STEP_LIST_DENSITY_REGULAR = 'regular';
export const STEP_LIST_DENSITY_COMFORTABLE = 'comfortable';

export type StepListDensity =
  | typeof STEP_LIST_DENSITY_COMPACT
  | typeof STEP_LIST_DENSITY_REGULAR
  | typeof STEP_LIST_DENSITY_COMFORTABLE;

/**
 * Visual variant. `icon` (default) shows filled circles with SVG
 * icons and vertical connector lines. `dot` shows plain small dots
 * with no connectors — matches the execution-log timeline style.
 */
export const STEP_LIST_VARIANT_ICON = 'icon';
export const STEP_LIST_VARIANT_DOT = 'dot';

export type StepListVariant =
  | typeof STEP_LIST_VARIANT_ICON
  | typeof STEP_LIST_VARIANT_DOT;

export const STEP_LIST_DENSITY_TOKENS: Record<
  StepListDensity,
  {
    indicatorSize: string;
    rowGap: string;
    titleFontSize: string;
    descriptionFontSize: string;
    metaFontSize: string;
    rowPadY: string;
  }
> = {
  [STEP_LIST_DENSITY_COMPACT]: {
    indicatorSize: '20px',
    rowGap: '6px',
    titleFontSize: '13px',
    descriptionFontSize: '11.5px',
    metaFontSize: '11px',
    rowPadY: '4px',
  },
  [STEP_LIST_DENSITY_REGULAR]: {
    indicatorSize: '24px',
    rowGap: '10px',
    titleFontSize: '14px',
    descriptionFontSize: '12.5px',
    metaFontSize: '11.5px',
    rowPadY: '6px',
  },
  [STEP_LIST_DENSITY_COMFORTABLE]: {
    indicatorSize: '28px',
    rowGap: '14px',
    titleFontSize: '14.5px',
    descriptionFontSize: '13px',
    metaFontSize: '12px',
    rowPadY: '10px',
  },
};
